import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Sidebar from '../components/DashboardSidebar';
import './StickersAdmin.css';

const API_URL = process.env.REACT_APP_API_URL || 'https://orders.fletaime.com';

export default function StickersAdmin() {
  const navigate = useNavigate();
  const { categoryId } = useParams();

  const [categories, setCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(true);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [creatingCategory, setCreatingCategory] = useState(false);
  const [renamingId, setRenamingId] = useState(null);
  const [renameValue, setRenameValue] = useState('');
  const [deletingCategoryId, setDeletingCategoryId] = useState(null);

  const [stickers, setStickers] = useState([]);
  const [loadingStickers, setLoadingStickers] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [deletingStickerId, setDeletingStickerId] = useState(null);

  const selectedCategory = categories.find(c => String(c.id) === String(categoryId));

  useEffect(() => {
    if (localStorage.getItem('dashboardAuth') !== 'true') {
      navigate('/dashboard/sign-in');
      return;
    }
    fetchCategories();
  }, [navigate]);

  const fetchCategories = () => {
    setLoadingCategories(true);
    fetch(`${API_URL}/api/sticker-categories`)
      .then(r => r.json())
      .then(data => {
        const list = Array.isArray(data) ? data : [];
        setCategories(list);
        setLoadingCategories(false);
        if (!categoryId && list.length > 0) {
          navigate(`/dashboard/stickers/${list[0].id}`, { replace: true });
        }
      })
      .catch(() => setLoadingCategories(false));
  };

  const fetchStickers = useCallback((catId) => {
    setLoadingStickers(true);
    fetch(`${API_URL}/api/sticker-categories/${catId}/stickers`)
      .then(r => r.json())
      .then(data => { setStickers(Array.isArray(data) ? data : []); setLoadingStickers(false); })
      .catch(() => setLoadingStickers(false));
  }, []);

  useEffect(() => {
    if (categoryId) fetchStickers(categoryId);
    else setStickers([]);
  }, [categoryId, fetchStickers]);

  const handleCreateCategory = async (e) => {
    e.preventDefault();
    if (!newCategoryName.trim()) return;
    setCreatingCategory(true);
    try {
      const res = await fetch(`${API_URL}/api/sticker-categories`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: newCategoryName.trim(), sort_order: categories.length }),
      });
      if (res.ok) {
        const created = await res.json();
        setCategories(prev => [...prev, { ...created, sticker_count: 0 }]);
        setNewCategoryName('');
        navigate(`/dashboard/stickers/${created.id}`);
      } else {
        alert('Gabim gjatë krijimit të kategorisë!');
      }
    } catch {
      alert('Gabim gjatë krijimit të kategorisë!');
    }
    setCreatingCategory(false);
  };

  const startRename = (cat) => {
    setRenamingId(cat.id);
    setRenameValue(cat.name);
  };

  const saveRename = async (cat) => {
    if (!renameValue.trim() || renameValue.trim() === cat.name) {
      setRenamingId(null);
      return;
    }
    try {
      const res = await fetch(`${API_URL}/api/sticker-categories/${cat.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: renameValue.trim() }),
      });
      if (res.ok) {
        const updated = await res.json();
        setCategories(prev => prev.map(c => c.id === cat.id ? { ...c, name: updated.name } : c));
      } else {
        alert('Gabim gjatë ndryshimit të emrit!');
      }
    } catch {
      alert('Gabim gjatë ndryshimit të emrit!');
    }
    setRenamingId(null);
  };

  const handleDeleteCategory = async (cat) => {
    if (!window.confirm(`A jeni i sigurt që doni ta fshini kategorinë "${cat.name}" bashkë me të gjithë stickers brenda saj?`)) return;
    setDeletingCategoryId(cat.id);
    try {
      const res = await fetch(`${API_URL}/api/sticker-categories/${cat.id}`, { method: 'DELETE' });
      if (res.ok) {
        const remaining = categories.filter(c => c.id !== cat.id);
        setCategories(remaining);
        if (String(categoryId) === String(cat.id)) {
          navigate(remaining.length > 0 ? `/dashboard/stickers/${remaining[0].id}` : '/dashboard/stickers', { replace: true });
        }
      } else {
        alert('Gabim gjatë fshirjes së kategorisë!');
      }
    } catch {
      alert('Gabim gjatë fshirjes së kategorisë!');
    }
    setDeletingCategoryId(null);
  };

  const handleUploadSvg = async (e) => {
    const file = e.target.files[0];
    e.target.value = '';
    if (!file || !categoryId) return;
    if (!/\.svg$/i.test(file.name)) {
      alert('Vetëm skedarë .svg lejohen!');
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('category_id', categoryId);
      const res = await fetch(`${API_URL}/api/stickers`, { method: 'POST', body: fd });
      if (res.ok) {
        const created = await res.json();
        setStickers(prev => [...prev, created]);
        setCategories(prev => prev.map(c => String(c.id) === String(categoryId) ? { ...c, sticker_count: (c.sticker_count || 0) + 1 } : c));
      } else {
        const err = await res.json().catch(() => ({}));
        alert(err.error || 'Gabim gjatë ngarkimit të stickerit!');
      }
    } catch {
      alert('Gabim gjatë ngarkimit të stickerit!');
    }
    setUploading(false);
  };

  const handleDeleteSticker = async (sticker) => {
    if (!window.confirm('A jeni i sigurt që doni ta fshini këtë sticker?')) return;
    setDeletingStickerId(sticker.id);
    try {
      const res = await fetch(`${API_URL}/api/stickers/${sticker.id}`, { method: 'DELETE' });
      if (res.ok) {
        setStickers(prev => prev.filter(s => s.id !== sticker.id));
        setCategories(prev => prev.map(c => String(c.id) === String(categoryId) ? { ...c, sticker_count: Math.max(0, (c.sticker_count || 1) - 1) } : c));
      } else {
        alert('Gabim gjatë fshirjes së stickerit!');
      }
    } catch {
      alert('Gabim gjatë fshirjes së stickerit!');
    }
    setDeletingStickerId(null);
  };

  return (
    <div className="dashboard-layout">
      <Sidebar />
      <div className="dashboard-content">
        <div className="pa-header">
          <h2>Stickers</h2>
        </div>

        <div className="sa-layout">
          <div className="sa-categories">
            <div className="sa-categories-title">Kategoritë</div>

            {loadingCategories ? (
              <div className="pa-loading">Duke ngarkuar...</div>
            ) : (
              <ul className="sa-category-list">
                {categories.map(cat => (
                  <li
                    key={cat.id}
                    className={String(cat.id) === String(categoryId) ? 'sa-category-item active' : 'sa-category-item'}
                  >
                    {renamingId === cat.id ? (
                      <input
                        className="sa-rename-input"
                        value={renameValue}
                        autoFocus
                        onChange={e => setRenameValue(e.target.value)}
                        onBlur={() => saveRename(cat)}
                        onKeyDown={e => { if (e.key === 'Enter') saveRename(cat); if (e.key === 'Escape') setRenamingId(null); }}
                      />
                    ) : (
                      <span className="sa-category-name" onClick={() => navigate(`/dashboard/stickers/${cat.id}`)}>
                        {cat.name} <span className="sa-category-count">({cat.sticker_count || 0})</span>
                      </span>
                    )}
                    <div className="sa-category-actions">
                      <button className="sa-icon-btn" title="Riemërto" onClick={() => startRename(cat)}>✎</button>
                      <button
                        className="sa-icon-btn sa-icon-btn-danger"
                        title="Fshi"
                        onClick={() => handleDeleteCategory(cat)}
                        disabled={deletingCategoryId === cat.id}
                      >
                        {deletingCategoryId === cat.id ? '...' : '✕'}
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}

            <form className="sa-new-category" onSubmit={handleCreateCategory}>
              <input
                placeholder="Kategori e re..."
                value={newCategoryName}
                onChange={e => setNewCategoryName(e.target.value)}
              />
              <button type="submit" disabled={creatingCategory || !newCategoryName.trim()}>
                {creatingCategory ? '...' : '+ Shto'}
              </button>
            </form>
          </div>

          <div className="sa-stickers">
            {!categoryId ? (
              <div className="pa-empty">Krijo ose zgjidh një kategori për të shtuar stickers.</div>
            ) : (
              <>
                <div className="sa-stickers-header">
                  <h3>{selectedCategory ? selectedCategory.name : ''}</h3>
                  <div
                    className={uploading ? 'sa-upload-btn sa-upload-btn-disabled' : 'sa-upload-btn'}
                    onClick={() => !uploading && document.getElementById('sa-file-input').click()}
                  >
                    {uploading ? 'Duke ngarkuar...' : '+ Ngarko SVG'}
                  </div>
                  <input
                    id="sa-file-input"
                    type="file"
                    accept=".svg,image/svg+xml"
                    style={{ display: 'none' }}
                    onChange={handleUploadSvg}
                  />
                </div>

                {loadingStickers ? (
                  <div className="pa-loading">Duke ngarkuar...</div>
                ) : stickers.length === 0 ? (
                  <div className="pa-empty">Nuk ka stickers në këtë kategori ende.</div>
                ) : (
                  <div className="sa-sticker-grid">
                    {stickers.map(s => (
                      <div className="sa-sticker-card" key={s.id}>
                        <div className="sa-sticker-thumb">
                          <img src={s.svg_url} alt={s.name || 'sticker'} />
                        </div>
                        <button
                          className="sa-sticker-delete"
                          onClick={() => handleDeleteSticker(s)}
                          disabled={deletingStickerId === s.id}
                        >
                          {deletingStickerId === s.id ? '...' : 'Fshi'}
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
